import { Component, type ErrorInfo, type ReactNode } from "react";

// Used at the root AND around the lazy Map / Draw modals so a crash there can't take the table down
// with it.
interface Props {
  children: ReactNode;
  // optional local fallback label (e.g. "map"); resets when `resetKey` changes
  label?: string;
  resetKey?: unknown;
  onReset?: () => void;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // clearing when the caller swaps context (e.g. opens a different field's map)
    if (this.state.error && prev.resetKey !== this.props.resetKey)
      this.setState({ error: null });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Errors are written to the console. Known limitation: there is no telemetry sink.
    console.error(
      "[ErrorBoundary]",
      this.props.label ?? "app",
      error,
      info.componentStack,
    );
  }

  private reset = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-full min-h-40 flex-col items-center justify-center gap-2 bg-canvas p-6 text-center">
        <p className="text-sm font-semibold text-fg">
          Something went wrong
          {this.props.label ? ` in the ${this.props.label}` : ""}.
        </p>
        <p className="max-w-md text-xs text-muted">
          {this.state.error.message || "Unexpected error."}
        </p>
        <div className="mt-1 flex gap-2">
          <button
            className="rounded border border-line px-3 py-1 text-xs font-medium text-muted hover:bg-hover"
            onClick={this.reset}
          >
            Dismiss
          </button>
          <button
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-canvas hover:bg-accent-hover"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
